import { useEffect, useRef, useState } from 'react'
import { AlertCircle, Check, Loader2, RotateCcw } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { apiJson, type ApprovalPreflight, type GuideRevision } from '@/lib/guide-admin'

export function GuideApprovalDialog({ publication, approved, open, onOpenChange, onComplete }: {
  publication: GuideRevision | null
  approved: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  onComplete: () => void
}) {
  const [preflight, setPreflight] = useState<ApprovalPreflight | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const mutationId = useRef('')
  const load = async () => {
    if (!publication) return
    setLoading(true); setError(''); mutationId.current = crypto.randomUUID()
    try {
      setPreflight(await apiJson<ApprovalPreflight>(`/api/guides/${publication.seed}/${publication.seedVersion}/${publication.revision}/approval-impact`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ approved }),
      }))
    } catch (reason) { setError(reason instanceof Error ? reason.message : String(reason)) }
    finally { setLoading(false) }
  }
  useEffect(() => { if (open) void load(); else setPreflight(null) }, [open, publication?.seed, publication?.seedVersion, publication?.revision, approved])
  const confirm = async () => {
    if (!publication || !preflight) return
    setSaving(true); setError('')
    try {
      await apiJson(`/api/guides/${publication.seed}/${publication.seedVersion}/${publication.revision}/approval`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({
          approved, expectedManifestHash: preflight.manifestHash,
          expectedApprovalRowVersion: preflight.approvalRowVersion,
          impactToken: preflight.impactToken, mutationId: mutationId.current,
        }),
      })
      onOpenChange(false); onComplete()
    } catch (reason) {
      const value = reason as Error & { status?: number; body?: ApprovalPreflight & { error?: string } }
      if (value.status === 409 && value.body?.impactToken) { setPreflight(value.body); mutationId.current = crypto.randomUUID() }
      setError(value.message)
    } finally { setSaving(false) }
  }
  return <Dialog open={open} onOpenChange={saving ? () => {} : onOpenChange}>
    <DialogContent>
      <DialogHeader><DialogTitle>{approved ? 'Approve guide publication?' : 'Unapprove guide publication?'}</DialogTitle><DialogDescription>{publication ? `${publication.seed} · application version ${publication.seedVersion} · revision ${publication.revision}` : ''}</DialogDescription></DialogHeader>
      {loading ? <div className="flex min-h-24 items-center justify-center"><Loader2 className="size-5 animate-spin"/></div> : preflight ? <div className="space-y-3 text-sm"><p>{preflight.impact.message}</p>{preflight.impact.policyRepair && <Alert><RotateCcw className="size-4"/><AlertDescription>{preflight.impact.policyRepair.reason}</AlertDescription></Alert>}<p className="break-all text-xs text-muted-foreground">Manifest: {preflight.manifestHash || 'unavailable'}</p></div> : null}
      {error && <Alert variant="destructive"><AlertCircle className="size-4"/><AlertDescription>{error}</AlertDescription></Alert>}
      <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button><Button variant={approved ? 'default' : 'destructive'} onClick={confirm} disabled={!preflight || loading || saving}>{saving ? <Loader2 className="mr-2 size-4 animate-spin"/> : approved ? <Check className="mr-2 size-4"/> : <RotateCcw className="mr-2 size-4"/>}{approved ? 'Approve' : 'Unapprove'}</Button></DialogFooter>
    </DialogContent>
  </Dialog>
}
